"use client";

import { useQuery } from "@apollo/client/react";
import clsx from "clsx";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { createContext, useContext } from "react";

import type { DocumentType } from "../../__generated__/gql";
import { graphql } from "../../__generated__/gql";
import {
  PAGES_WITH_HIDDEN_GLOBAL_FOOTER,
  PAGES_WITH_HIDDEN_HEADER_ELEMENTS,
  PAGES_WITH_HIDDEN_HEADER_ELEMENTS_EXACT,
  PAGES_WITH_HIDDEN_HEADER_ELEMENTS_IN_MOBILE,
  PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE,
} from "../../constants";
import { LOCALE_PREFIX_RE } from "../../i18n/settings";
import { isI18nTargetPage } from "../../i18n/target-pages";
import GlobalFooter from "../_components/global-footer";
import { LanguageSelector } from "../_components/language-selector";
import {
  TranslationGuideDialog,
  FloatingTranslationButton,
} from "../_components/translation-guide-dialog";
import { Link } from "../_components/ui/link";
import GlobalMenu from "../_contexts/global-menu";
import HeaderReservationActions from "../_contexts/header-reservation-actions";

const Query = graphql(`
  query PermissionOnDefaultLayout {
    maybeViewer {
      id
      hasReservablePlan
      organization {
        id
      }
    }
  }
`);

export default function Layout({ children }: { children: React.ReactNode }) {
  const { data: statusData, loading } = useQuery(Query);
  const currentPath = usePathname();
  const normalizedPath = currentPath.replace(LOCALE_PREFIX_RE, "/");
  const hideHeaderElements =
    PAGES_WITH_HIDDEN_HEADER_ELEMENTS.some(
      (hiddenPath) =>
        normalizedPath === hiddenPath ||
        normalizedPath.startsWith(`${hiddenPath}/`),
    ) ||
    PAGES_WITH_HIDDEN_HEADER_ELEMENTS_EXACT.some(
      (hiddenPath) => normalizedPath === hiddenPath,
    );
  const hideHeaderElementsInMobile =
    PAGES_WITH_HIDDEN_HEADER_ELEMENTS_IN_MOBILE.some(
      (hiddenPath) =>
        normalizedPath === hiddenPath ||
        normalizedPath.startsWith(`${hiddenPath}/`),
    );
  const hideGlobalFooter = PAGES_WITH_HIDDEN_GLOBAL_FOOTER.some(
    (hiddenPath) =>
      normalizedPath === hiddenPath ||
      normalizedPath.startsWith(`${hiddenPath}/`),
  );
  const isHeaderUnstickyInMobile = PAGES_WITH_UNSTICKY_HEADER_IN_MOBILE.some(
    (hiddenPath) =>
      normalizedPath === hiddenPath ||
      normalizedPath.startsWith(`${hiddenPath}/`),
  );
  const canViewCalendar = statusData?.maybeViewer?.hasReservablePlan ?? false;
  const isLoggedin = statusData?.maybeViewer?.id !== undefined;
  // DXPJ 4 ページ配下 (/areas/* = エリア/拠点/部屋詳細/おすすめスポット) では、
  // 共通ヘッダー背景色を左右カラムと同じ dx-surface-base に揃える。
  const isDxAreaPage = normalizedPath.startsWith("/areas");

  return (
    <ReservationsProvider>
      <div
        className={clsx(
          "hidden-in-nativeapp h-global-header-min md:h-global-header-md z-20 flex flex-none items-center justify-between",
          // SP ではヘッダー固定を解除したいページがあるため、対象ページのみ sticky を md 以上に限定する。
          isHeaderUnstickyInMobile ? "md:sticky md:top-0" : "sticky top-0",
          // DXPJ ページではロゴ左位置を pane コンテンツの inset (16px / xs+ 32px) に揃える (DXPJ-62 roGEJ)。
          isDxAreaPage
            ? "bg-dx-surface-base xs:pl-[32px] pl-[16px]"
            : "bg-slightbeige pl-[30px] md:pl-[40px]",
        )}
      >
        <Link
          data-analytics-id="global_header_clicked_logo"
          href={isLoggedin ? "/member" : "/"}
          className="flex"
        >
          <Image
            src="/i/sanu2ndhome_logo.svg"
            alt="SANU 2nd Home Logo"
            width={129}
            height={19}
          />
        </Link>
        <div className="flex h-full items-center">
          <LanguageSelector />
          {!hideHeaderElements && (
            <>
              <div className="hidden h-full md:flex">
                <HeaderReservationActions
                  canViewReservationCalendar={canViewCalendar}
                />
              </div>
              <GoToReserve
                canViewReservationCalendar={canViewCalendar}
                disabled={loading}
              />
              <GlobalMenu />
            </>
          )}
        </div>
      </div>
      {/* モバイル版の場合 */}
      {!hideHeaderElements && !hideHeaderElementsInMobile && (
        <div
          className={clsx(
            "hidden-in-nativeapp h-global-header-min sticky top-0 z-20 flex md:hidden",
            isDxAreaPage && "bg-dx-surface-base",
          )}
        >
          <HeaderReservationActions
            canViewReservationCalendar={canViewCalendar}
          />
        </div>
      )}
      {/* コンテンツが上に重なるのを避けるため、global header より下になる Stacking context を作る */}
      <div className="relative z-10">
        <StackingLayer />
        {children}
        {!hideGlobalFooter && <GlobalFooter />}
      </div>
      {!isI18nTargetPage(currentPath) && <TranslationGuideDialog />}
      {!isI18nTargetPage(currentPath) && <FloatingTranslationButton />}
    </ReservationsProvider>
  );
}

function StackingLayer() {
  return (
    <div className="hidden-in-nativeapp absolute top-0 z-1 h-px w-full"></div>
  );
}

function GoToReserve({
  canViewReservationCalendar,
  disabled = false,
}: {
  canViewReservationCalendar: boolean | undefined;
  disabled?: boolean;
}) {
  const href =
    canViewReservationCalendar === false
      ? "https://stay.sa-nu.com/"
      : "/reservation-form";
  const label =
    canViewReservationCalendar === false ? "宿泊サイト TOP" : "予約カレンダー";

  const className =
    "flex h-[40px] min-w-[72px] items-center justify-center rounded-[4px] bg-skogecha md:h-full md:min-w-[100px] md:rounded-none";

  return disabled ? (
    <div className={clsx(className, "cursor-progress")}></div>
  ) : (
    <Link
      data-analytics-id="global_header_clicked_reservation_calendar"
      href={href}
      className={clsx(className, "hover:opacity-80")}
    >
      <div className="border-sorange text-swhite border-b px-[8px] pt-[4px] pb-[5px] text-[10px] font-bold whitespace-nowrap">
        {label}
      </div>
    </Link>
  );
}

// Reservations
//-----------------------------------

const ReservationsQuery = graphql(`
  query MyReservations {
    maybeViewer {
      id
      accommodationReservations {
        id
        stayStatus
        canCheckIn
        canCheckOut
        accessCode
        currentEarlyCheckInFee
        ...ReservationBox
      }
    }
  }
`);

type Reservations = NonNullable<
  DocumentType<typeof ReservationsQuery>["maybeViewer"]
>["accommodationReservations"];

export const ReservationsContext = createContext<{
  reservations: Reservations | null;
  refetch: () => void;
}>({
  reservations: null,
  refetch: () => {},
});

function ReservationsProvider({ children }: { children: React.ReactNode }) {
  const { data, refetch } = useQuery(ReservationsQuery);
  const reservations = data?.maybeViewer?.accommodationReservations ?? null;
  return (
    <ReservationsContext.Provider value={{ reservations, refetch }}>
      {children}
    </ReservationsContext.Provider>
  );
}

export function useRefetchReservations() {
  return useContext(ReservationsContext).refetch;
}
